import { AxiosError } from 'axios'
import * as Sentry from '@sentry/react'
import env from '../constants/auth.constants'

export enum OrderType {
    Ascending = 'ASCENDING',
    Descending = 'DESENDING',
}

// eslint-disable-next-line
type RowExtractor<T> = (item: T) => any[]

export const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
        /**
         * Workaround for browser hard reload
         */
        await navigator.serviceWorker.getRegistration().then(reg => {
            if (reg?.active && !navigator.serviceWorker.controller) {
                window.location.reload()
            }
        })

        return navigator.serviceWorker.register(
            import.meta.env.MODE === 'production' ? '/sw.js' : '/dev-sw.js?dev-sw'
        )
    }
}

export const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

export const sortDropdownList = <T, K extends keyof T>(
    array: T[],
    property: K,
    order?: OrderType
): T[] => {
    if (order === OrderType.Descending) {
        return array.slice().sort((a, b) => {
            return String(b[property]).localeCompare(String(a[property]))
        })
    }
    return array.slice().sort((a, b) => {
        return String(a[property]).localeCompare(String(b[property]))
    })
}

export const downloadJsonFile = (data: object, filename: string) => {
    const file = new Blob([JSON.stringify(data, undefined, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    const url = URL.createObjectURL(file)
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
    }, 0)
}

export const downloadCsvFile = (csvContent: string, fileName: string) => {
    const file = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    const url = URL.createObjectURL(file)
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
    }, 0)
}

export const convertToCSV = <T>(
    data: T[] | undefined,
    headers: string[],
    rowExtractor: RowExtractor<T>
): string => {
    // eslint-disable-next-line
    const escapeCSVValue = (value: any) => `"${value.toString().replace(/"/g, '""')}"`

    const rows = data ? data.map(rowExtractor).map(row => row.map(escapeCSVValue)) : []
    return [headers.map(escapeCSVValue), ...rows].map(row => row.join(',')).join('\n')
}

export const batchProcessPromises = async <T, K>(
    arr: T[],
    cb: (item: T, index: number) => Promise<K>,
    batchSize: number
): Promise<K[]> => {
    const batches = Math.ceil(arr.length / batchSize)

    const results: K[] = []

    for (let i = 0; i < batches; i++) {
        const batch = arr.slice(i * batchSize, (i + 1) * batchSize)
        const result = await Promise.all(
            batch.map((item, index) => cb(item, index + i * batchSize))
        )
        results.push(...result)
    }

    return results
}

export const sanitizeError = (error: AxiosError | Error) => {
    if (error instanceof AxiosError) {
        return (
            (error.response?.data as { error: { message: string; code?: string } }) ?? {
                error: { message: 'Something went wrong' },
            }
        )
    } else {
        return { error: { message: error.message ?? 'Something went wrong' } }
    }
}

export const initialiseSentry = () => {
    Sentry.init({
        dsn: 'https://e3dc20f82771f60f316b0be392e9454f@o132323.ingest.us.sentry.io/4508143328428032',
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 1.0,
        tracePropagationTargets: [/^https:\/\/playment\.io/],
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        enabled: process.env.ENV_BUILD_ENVIRONMENT === env.PRODUCTION,
    })
}

export const getNextWeeklyDate = (startDate: Date): Date => {
    const today = new Date()
    const nextDate = new Date(startDate)

    while (nextDate < today) {
        nextDate.setDate(nextDate.getDate() + 7)
    }

    return nextDate
}

export const getFormattedTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
}
